import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../../store/gameStore';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Search, MessageSquare, FileSearch, MapPin, HelpCircle, AlertTriangle, Menu, Home, Archive, Grid3X3, Users, Clock, Network, FolderOpen, KeyRound, Check } from 'lucide-react';
// Toast disabled for tutorial - was blocking UI elements
import { gameAPI } from '../../services/gameAPI';
import { CaseFile } from './CaseFile';
import { EvidenceBoard } from './evidence/EvidenceBoard';
import { SuspectWall } from './SuspectWall';
import { Timeline } from './Timeline';
import { RelationshipMap } from './RelationshipMap';
import { InterrogationModal } from './InterrogationModal';
import { ExamineModal } from './ExamineModal';
import { AccusationModal } from '../modals/AccusationModal';
import { CipherModal } from './CipherModal';
import { TutorialCipherModal } from './TutorialCipherModal';

/**
 * Tutorial Cipher Data - Pre-scripted cipher puzzles for each theme
 * These match the cipher data embedded in tutorial JSON narratives
 */
const TUTORIAL_CIPHER_DATA = {
  noir: {
    ciphertext: 'FVHFOF IJE UIF FWJEFODF',
    hints: [
      'Shift by one to find the answer',
      'Always count one ahead',
      'The number 1 is circled repeatedly',
      'One notch scratched into the metal'
    ],
    expectedSolution: 'EUGENE HID THE EVIDENCE'
  },
  '90s_nyc': {
    ciphertext: 'HZPFIZ OVUG ZG NRWMRTSG',
    hints: [
      'A becomes Z, Z becomes A. Everything reversed',
      'Reverse the alphabet to decode',
      'The alphabet runs backwards here',
      'Mirror the letters to find truth'
    ],
    expectedSolution: 'SAKURA LEFT AT MIDNIGHT'
  },
  miami_vice: {
    ciphertext: 'DVN WUHQW DERXW QLJKW',
    hints: [
      'Shift by three to find the answer',
      'Shift by three to find the answer',
      'The number 3 is circled repeatedly',
      'Three notches scratched into the metal'
    ],
    expectedSolution: 'ASK TRENT ABOUT NIGHT'
  }
};

/**
 * TutorialActionBar - Decoupled client-side tutorial system
 * - Same visual layout as the real game
 * - NO API calls - processes actions using tutorialScript data
 * - Supports preSelectedAction from RightPanel clicks (like real game)
 * - Interview and Examine already work via modals (unchanged)
 */
export const TutorialActionBar = ({
  onActionComplete,
  onActionSelect,
  onModalOpen,
  onModalClose,
  currentStepModalInfo,
  tutorialScript = [],
  tutorialNarratives = {},
  currentStep = 1
}) => {
  const navigate = useNavigate();
  const gameState = useGameStore();
  const {
    suspects,
    locations,
    documents,
    discovered,
    updateGameState,
    selectedTheme,
    addMessage,
    sessionId,
    turnCount,
    preSelectedAction,
    preSelectedTarget,
    setPreSelectedAction,
    cipherState
  } = gameState;

  // Belt-and-braces theme normalization. TutorialPage normalizes 'vice' →
  // 'miami_vice' at store-write, but this guards against stale bundles or
  // direct prop mis-sets so every theme-keyed lookup in this component
  // (cipher modal, interrogation recommendedApproach, etc.) resolves.
  const normalizedTheme = selectedTheme === 'vice' ? 'miami_vice' : selectedTheme;

  const [selectedAction, setSelectedAction] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showCaseFile, setShowCaseFile] = useState(false);
  const [showEvidenceBoard, setShowEvidenceBoard] = useState(false);
  const [showSuspectWall, setShowSuspectWall] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showRelationshipMap, setShowRelationshipMap] = useState(false);
  const [showInterrogation, setShowInterrogation] = useState(false);
  const [interrogationSuspect, setInterrogationSuspect] = useState(null);
  const [interrogationRoundsCompleted, setInterrogationRoundsCompleted] = useState({});
  const [showExamineModal, setShowExamineModal] = useState(false);
  const [examineLocation, setExamineLocation] = useState(null);
  const [showAccusation, setShowAccusation] = useState(false);
  const [showCipherModal, setShowCipherModal] = useState(false);

  // Auto-populate action and target from pre-selection (when user clicks in Right Panel)
  useEffect(() => {
    if (preSelectedAction) {
      setSelectedAction(preSelectedAction);
      setSelectedTarget(preSelectedTarget || '');
    }
  }, [preSelectedAction, preSelectedTarget]);

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || '90s_nyc'];

  // Actions list - matches real ActionBar exactly
  const actions = [
    { id: 'examine', label: 'Examine Location', icon: Search },
    { id: 'interview', label: 'Interview Suspect', icon: MessageSquare },
    { id: 'read', label: 'Read Document', icon: FileSearch },
    { id: 'travel', label: 'Travel To', icon: MapPin },
    { id: 'consult_guide', label: 'Consult Guide', icon: HelpCircle },
  ];

  // Tutorial dropdown click-leak prevention:
  // When the current tutorial step expects a specific action selection
  // (e.g. "select_examine"), only the correct option should be interactive.
  // Wrong options are marked disabled at the component level so that beta
  // testers can't intentionally click off-target inside the open dropdown.
  // Mapping: step.action === 'select_<id>'  =>  required action id is <id>.
  const tutorialScriptStep = (tutorialScript && currentStep)
    ? tutorialScript[currentStep - 1]
    : null;
  const requiredActionId = (() => {
    const a = tutorialScriptStep?.action;
    if (typeof a !== 'string') return null;
    const m = a.match(/^select_([a-z_]+)$/);
    return m ? m[1] : null;
  })();
  const lockActionDropdown = !!requiredActionId;

  // Get targets based on selected action - matches real ActionBar
  const getTargets = () => {
    switch (selectedAction) {
      case 'interview': {
        const discoveredSuspects = (suspects ?? []).filter(s => discovered?.suspects?.includes(s.id));
        return discoveredSuspects.map(s => ({ value: s.id, label: s.name }));
      }
      case 'examine':
      case 'travel': {
        const discoveredLocations = (locations ?? []).filter(loc => discovered?.locations?.includes(loc.id));
        return discoveredLocations.map(loc => ({ value: loc.id, label: loc.name }));
      }
      case 'read': {
        const discoveredDocuments = (documents ?? []).filter(d => discovered?.documents?.includes(d.id));
        return discoveredDocuments.map(d => ({ value: d.id, label: d.title }));
      }
      default:
        return [];
    }
  };

  // Handle action execution - CLIENT-SIDE processing using script data
  const handleAction = async () => {
    if (!selectedAction) {
      return;
    }

    if (selectedAction !== 'consult_guide' && !selectedTarget) {
      return;
    }

    // For interview actions, open the interrogation modal
    if (selectedAction === 'interview') {
      const suspect = (suspects || []).find(s => s.id === selectedTarget);
      if (suspect) {
        setInterrogationSuspect(suspect);
        setShowInterrogation(true);
        if (preSelectedAction) {
          setPreSelectedAction(null, null);
        }
        // Emit event for tutorial step advancement
        if (onActionComplete) {
          onActionComplete('action_interview', selectedTarget, { success: true });
        }
      }
      return;
    }

    // For examine actions, open the examine modal (unchanged)
    if (selectedAction === 'examine') {
      const location = (locations || []).find(l => l.id === selectedTarget);
      if (location) {
        setExamineLocation(location);
        setShowExamineModal(true);
        if (onActionComplete) {
          onActionComplete('examine', selectedTarget, { success: true });
        }
        if (preSelectedAction) {
          setPreSelectedAction(null, null);
        }
      }
      return;
    }

    setProcessing(true);

    try {
      // Get current step's script data (step numbers are 1-indexed, array is 0-indexed)
      const scriptStep = tutorialScript[currentStep - 1];

      if (!scriptStep) {
        console.warn(`[TutorialActionBar] No script data for step ${currentStep}`);
        setProcessing(false);
        return;
      }

      // Use the narrative from script
      const narrative = scriptStep.narrative || 'You continue your investigation...';

      // Process reveals from script
      const revealed = scriptStep.reveals || {
        suspects: [],
        clues: [],
        locations: [],
        documents: []
      };

      // Get current discovered state
      const currentDiscovered = gameState.discovered || {
        suspects: [],
        clues: [],
        locations: [],
        documents: []
      };

      // Merge revealed items into discovered
      const newDiscovered = {
        suspects: [...new Set([...currentDiscovered.suspects, ...revealed.suspects])],
        clues: [...new Set([...currentDiscovered.clues, ...revealed.clues])],
        locations: [...new Set([...currentDiscovered.locations, ...revealed.locations])],
        documents: [...new Set([...currentDiscovered.documents, ...revealed.documents])]
      };

      // Calculate newly discovered items (for "new" indicators)
      const newSuspects = revealed.suspects.filter(id => !currentDiscovered.suspects.includes(id));
      const newClues = revealed.clues.filter(id => !currentDiscovered.clues.includes(id));
      const newLocations = revealed.locations.filter(id => !currentDiscovered.locations.includes(id));
      const newDocuments = revealed.documents.filter(id => !currentDiscovered.documents.includes(id));

      // Update narrative
      updateGameState({ narrativeText: narrative });
      addMessage(selectedAction, selectedTarget, narrative);

      // Update discovered items
      updateGameState({
        discovered: newDiscovered,
        newlyDiscovered: {
          suspects: [...(gameState.newlyDiscovered?.suspects || []), ...newSuspects],
          clues: [...(gameState.newlyDiscovered?.clues || []), ...newClues],
          locations: [...(gameState.newlyDiscovered?.locations || []), ...newLocations],
          documents: [...(gameState.newlyDiscovered?.documents || []), ...newDocuments],
        }
      });

      // Update current location for travel
      if (selectedAction === 'travel') {
        updateGameState({ currentLocation: selectedTarget });
        const location = (locations || []).find(l => l.id === selectedTarget);
        if (location?.image) {
          updateGameState({ currentImage: location.image });
        }
      }

      // Update turn count
      updateGameState({ turnCount: (turnCount || 0) + 1 });

      // Notify parent for step advancement
      if (onActionComplete) {
        onActionComplete(selectedAction, selectedTarget, { success: true, narrative, revealed });
      }

      // Clear pre-selection after successful action
      if (preSelectedAction) {
        setPreSelectedAction(null, null);
      }

      setSelectedTarget('');
    } catch (error) {
      console.error('[TutorialActionBar] Failed to process action:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Handle accuse action - open the accusation modal
  const handleAccuse = () => {
    setShowAccusation(true);
    // Notify parent to advance tutorial (triggers on 'modal_accusation_open')
    if (onActionComplete) {
      onActionComplete('accuse_modal_open', null, { success: true });
    }
  };

  // Handle interrogation completion
  const handleInterrogationComplete = (result) => {
    if (!result) return;

    // Track rounds completed for this suspect (count all rounds that were done)
    if (interrogationSuspect) {
      const roundsJustCompleted = result.allNarratives?.length || 1;
      setInterrogationRoundsCompleted(prev => ({
        ...prev,
        [interrogationSuspect.id]: (prev[interrogationSuspect.id] || 0) + roundsJustCompleted
      }));
    }

    // Add to message history - save each round's narrative separately if available
    if (interrogationSuspect) {
      if (result.allNarratives && result.allNarratives.length > 0) {
        // Save each round's narrative as separate message
        result.allNarratives.forEach(narrative => {
          if (narrative) addMessage('interview', interrogationSuspect.id, narrative);
        });
      } else if (result.narrative) {
        addMessage('interview', interrogationSuspect.id, result.narrative);
      }
    }

    // Update narrative display with most recent narrative and turn count
    const displayNarrative = result.allNarratives 
      ? result.allNarratives[result.allNarratives.length - 1] 
      : result.narrative;
    updateGameState({
      narrativeText: displayNarrative,
      turnCount: (turnCount || 0) + (result.allNarratives?.length || 1)
    });

    // Update discovered items from interrogation
    if (result.discovered) {
      // Calculate newly discovered items (for "new" indicators)
      const currentDiscovered = gameState.discovered || {
        suspects: [],
        clues: [],
        locations: [],
        documents: []
      };

      const newSuspects = (result.discovered.suspects || []).filter(id => !currentDiscovered.suspects.includes(id));
      const newClues = (result.discovered.clues || []).filter(id => !currentDiscovered.clues.includes(id));
      const newLocations = (result.discovered.locations || []).filter(id => !currentDiscovered.locations.includes(id));
      const newDocuments = (result.discovered.documents || []).filter(id => !currentDiscovered.documents.includes(id));

      updateGameState({
        discovered: result.discovered,
        newlyDiscovered: {
          suspects: [...(gameState.newlyDiscovered?.suspects || []), ...newSuspects],
          clues: [...(gameState.newlyDiscovered?.clues || []), ...newClues],
          locations: [...(gameState.newlyDiscovered?.locations || []), ...newLocations],
          documents: [...(gameState.newlyDiscovered?.documents || []), ...newDocuments],
        }
      });
    }

    // Notify parent for step advancement (only called once now)
    if (onActionComplete) {
      onActionComplete('interview', interrogationSuspect?.id, result);
    }

    setSelectedTarget('');
    // No toast in tutorial mode
  };

  // Menu dropdown content - shared between mobile/tablet and desktop
  const menuContent = (
    <DropdownMenuContent align="start" className="bg-slate-900 border-white/20">
      <DropdownMenuItem
        onClick={() => navigate('/')}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Home className="w-4 h-4 mr-2" />
        Main Menu
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => navigate('/archive')}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Archive className="w-4 h-4 mr-2" />
        Case Archive
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-white/10" />
      <DropdownMenuItem
        onClick={() => setShowTimeline(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Clock className="w-4 h-4 mr-2" />
        Timeline
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => setShowRelationshipMap(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Network className="w-4 h-4 mr-2" />
        Relationships
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-black/60 backdrop-blur-md p-2 md:p-3">
      {/* Mobile Layout - stacked rows (hidden on md+) */}
      <div className="flex flex-col gap-2 md:hidden">
        {/* Row 1: Menu + Quick Actions + Accuse */}
        <div className="flex items-center gap-2">
          {/* Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-touch min-h-touch bg-black/40 border-white/20 text-white hover:bg-white/10"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            {menuContent}
          </DropdownMenu>

          {/* Quick Action Buttons - flex to fill space */}
          <Button
            onClick={() => {
              setShowCaseFile(true);
              if (onModalOpen) onModalOpen('casefile');
              if (onActionComplete) onActionComplete('modal_casefile_opened', null, { success: true });
            }}
            variant="outline"
            className="min-h-touch flex-1 bg-black/40 border-white/20 text-white hover:bg-white/10 text-xs"
            style={{ borderColor: `${themeAccent}40` }}
            data-tutorial="casefile-button"
          >
            <FolderOpen className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => {
              setShowEvidenceBoard(true);
              if (onModalOpen) onModalOpen('evidence');
              if (onActionComplete) onActionComplete('modal_evidence_opened', null, { success: true });
            }}
            variant="outline"
            className="min-h-touch flex-1 bg-black/40 border-white/20 text-white hover:bg-white/10 text-xs"
            style={{ borderColor: `${themeAccent}40` }}
            data-tutorial="evidence-button"
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => {
              setShowSuspectWall(true);
              if (onModalOpen) onModalOpen('suspects');
              if (onActionComplete) onActionComplete('modal_suspects_opened', null, { success: true });
            }}
            variant="outline"
            className="min-h-touch flex-1 bg-black/40 border-white/20 text-white hover:bg-white/10 text-xs"
            style={{ borderColor: `${themeAccent}40` }}
            data-tutorial="suspect-wall-button"
          >
            <Users className="w-4 h-4" />
          </Button>

          {/* Cipher Button - only shows when unlocked */}
          {cipherState?.unlocked && (
            <Button
              onClick={() => {
                setShowCipherModal(true);
                // Emit tutorial event when cipher button is clicked
                if (onActionComplete) {
                  onActionComplete('cipher_button_clicked', null, { success: true });
                }
              }}
              variant="outline"
              className={`min-h-touch flex-1 text-xs ${
                cipherState?.solved
                  ? 'bg-green-900/40 border-green-500/40 text-green-400 hover:bg-green-900/60'
                  : 'bg-black/40 text-white hover:bg-white/10'
              }`}
              style={{ borderColor: cipherState?.solved ? undefined : `${themeAccent}40` }}
              data-tutorial="cipher-button"
            >
              {cipherState?.solved ? (
                <Check className="w-4 h-4" />
              ) : (
                <KeyRound className="w-4 h-4" style={{ color: themeAccent }} />
              )}
            </Button>
          )}

          {/* Accuse Button */}
          <Button
            onClick={handleAccuse}
            variant="destructive"
            className="min-h-touch px-3 font-semibold bg-red-600 hover:bg-red-700"
            data-tutorial="accuse-button"
          >
            <AlertTriangle className="w-4 h-4" />
          </Button>
        </div>

        {/* Row 2: Action Selection + Target + Execute */}
        <div className="flex items-center gap-2">
          {/* Action Selection */}
          <Select value={selectedAction} onValueChange={(val) => {
            setSelectedAction(val);
            if (onActionSelect) onActionSelect(val);
          }}>
            <SelectTrigger
              className="min-h-touch flex-1 bg-black/40 border-white/20 text-white text-xs"
              data-tutorial="action-dropdown"
              data-action={selectedAction || undefined}
            >
              <SelectValue placeholder="Choose action..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/20">
              {actions.map((action) => {
                const Icon = action.icon;
                const isLockedOut = lockActionDropdown && action.id !== requiredActionId;
                return (
                  <SelectItem
                    key={action.id}
                    value={action.id}
                    className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
                    data-tutorial={`${action.id}-option`}
                    data-tutorial-correct={lockActionDropdown && action.id === requiredActionId ? 'true' : undefined}
                    disabled={isLockedOut}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {action.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Target Selection - only shows when action needs target */}
          {selectedAction && selectedAction !== 'consult_guide' && (
            <Select value={selectedTarget} onValueChange={setSelectedTarget}>
              <SelectTrigger
                className="min-h-touch flex-[1.5] bg-black/40 border-2 border-white/30 text-white shadow-lg"
                data-tutorial="target-dropdown"
              >
                <div className="flex items-center gap-2 w-full overflow-hidden">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: themeAccent }}></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: themeAccent }}></span>
                  </span>
                  <span className="truncate text-xs">
                    <SelectValue placeholder="Select target..." />
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/20">
                {getTargets().map((target) => (
                  <SelectItem
                    key={target.value}
                    value={target.value}
                    className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
                    data-tutorial={`target-${target.value}`}
                  >
                    {target.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Execute Button */}
          <Button
            onClick={handleAction}
            disabled={processing}
            className="min-h-touch px-4 font-semibold"
            style={{ backgroundColor: themeAccent }}
            data-tutorial="execute-button"
            data-action={selectedAction || undefined}
          >
            {processing ? '...' : 'Go'}
          </Button>
        </div>
      </div>

      {/* Desktop Layout - single row (hidden below md) */}
      <div className="hidden md:flex items-center gap-2">
        {/* Menu - always on left */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="bg-black/40 border-white/20 text-white hover:bg-white/10"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          {menuContent}
        </DropdownMenu>

        {/* Spacer - pushes everything else to the right */}
        <div className="flex-1" />

        {/* Quick Action Buttons */}
        <Button
          onClick={() => {
            setShowCaseFile(true);
            if (onModalOpen) onModalOpen('casefile');
            if (onActionComplete) onActionComplete('modal_casefile_opened', null, { success: true });
          }}
          variant="outline"
          className="bg-black/40 border-white/20 text-white hover:bg-white/10"
          style={{ borderColor: `${themeAccent}40` }}
          data-tutorial="casefile-button"
        >
          <FolderOpen className="w-4 h-4 mr-1" />
          Case File
        </Button>

        <Button
          onClick={() => {
            setShowEvidenceBoard(true);
            if (onModalOpen) onModalOpen('evidence');
            if (onActionComplete) onActionComplete('modal_evidence_opened', null, { success: true });
          }}
          variant="outline"
          className="bg-black/40 border-white/20 text-white hover:bg-white/10"
          style={{ borderColor: `${themeAccent}40` }}
          data-tutorial="evidence-button"
        >
          <Grid3X3 className="w-4 h-4 mr-1" />
          Evidence
        </Button>

        <Button
          onClick={() => {
            setShowSuspectWall(true);
            if (onModalOpen) onModalOpen('suspects');
            if (onActionComplete) onActionComplete('modal_suspects_opened', null, { success: true });
          }}
          variant="outline"
          className="bg-black/40 border-white/20 text-white hover:bg-white/10"
          style={{ borderColor: `${themeAccent}40` }}
          data-tutorial="suspect-wall-button"
        >
          <Users className="w-4 h-4 mr-1" />
          Suspects
        </Button>

        {/* Cipher Button - only shows when unlocked */}
        {cipherState?.unlocked && (
          <Button
            onClick={() => {
              setShowCipherModal(true);
              // Emit tutorial event when cipher button is clicked
              if (onActionComplete) {
                onActionComplete('cipher_button_clicked', null, { success: true });
              }
            }}
            variant="outline"
            className={`${
              cipherState?.solved
                ? 'bg-green-900/40 border-green-500/40 text-green-400 hover:bg-green-900/60'
                : 'bg-black/40 border-white/20 text-white hover:bg-white/10'
            }`}
            style={{ borderColor: cipherState?.solved ? undefined : `${themeAccent}40` }}
            data-tutorial="cipher-button"
          >
            {cipherState?.solved ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Decoded
              </>
            ) : (
              <>
                <KeyRound className="w-4 h-4 mr-1" style={{ color: themeAccent }} />
                Cipher
              </>
            )}
          </Button>
        )}

        {/* Divider */}
        <div className="h-8 w-px bg-white/20 mx-1" />

        {/* Action Selection */}
        <Select value={selectedAction} onValueChange={(val) => {
          setSelectedAction(val);
          if (onActionSelect) onActionSelect(val);
        }}>
          <SelectTrigger
            className="w-48 bg-black/40 border-white/20 text-white"
            data-tutorial="action-dropdown"
            data-action={selectedAction || undefined}
          >
            <SelectValue placeholder="Choose action..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/20">
            {actions.map((action) => {
              const Icon = action.icon;
              const isLockedOut = lockActionDropdown && action.id !== requiredActionId;
              return (
                <SelectItem
                  key={action.id}
                  value={action.id}
                  className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
                  data-tutorial={`${action.id}-option`}
                  data-tutorial-correct={lockActionDropdown && action.id === requiredActionId ? 'true' : undefined}
                  disabled={isLockedOut}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {action.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Target Selection - only shows when action needs target */}
        {selectedAction && selectedAction !== 'consult_guide' && (
          <Select value={selectedTarget} onValueChange={setSelectedTarget}>
            <SelectTrigger
              className="w-56 bg-black/40 border-2 border-white/30 text-white shadow-lg"
              data-tutorial="target-dropdown"
            >
              <div className="flex items-center gap-2 w-full overflow-hidden">
                <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: themeAccent }}></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: themeAccent }}></span>
                </span>
                <span className="truncate">
                  <SelectValue placeholder="Select target..." />
                </span>
              </div>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/20">
              {getTargets().map((target) => (
                <SelectItem
                  key={target.value}
                  value={target.value}
                  className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
                  data-tutorial={`target-${target.value}`}
                >
                  {target.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Execute Button */}
        <Button
          onClick={handleAction}
          disabled={processing}
          className="px-6 font-semibold"
          style={{ backgroundColor: themeAccent }}
          data-tutorial="execute-button"
          data-action={selectedAction || undefined}
        >
          {processing ? '...' : 'Go'}
        </Button>

        {/* Accuse Button */}
        <Button
          onClick={handleAccuse}
          variant="destructive"
          className="px-4 font-semibold bg-red-600 hover:bg-red-700"
          data-tutorial="accuse-button"
        >
          <AlertTriangle className="w-4 h-4 mr-1.5" />
          Accuse
        </Button>
      </div>

      {/* Case File Dialog */}
      <CaseFile
        open={showCaseFile}
        onClose={() => {
          setShowCaseFile(false);
          if (onModalClose && currentStepModalInfo?.closeToAdvance) {
            onModalClose('casefile');
          }
        }}
        tutorialInfo={showCaseFile ? currentStepModalInfo : null}
      />

      {/* Evidence Board Dialog */}
      <EvidenceBoard
        open={showEvidenceBoard}
        onClose={() => {
          setShowEvidenceBoard(false);
          if (onModalClose && currentStepModalInfo?.closeToAdvance) {
            onModalClose('evidence');
          }
        }}
        tutorialInfo={showEvidenceBoard ? currentStepModalInfo : null}
      />

      {/* Suspect Wall Dialog - simplified for tutorial */}
      <SuspectWall
        open={showSuspectWall}
        onClose={() => {
          setShowSuspectWall(false);
          if (onModalClose && currentStepModalInfo?.closeToAdvance) {
            onModalClose('suspects');
          }
        }}
        onStartInterrogation={() => {
          // No toast in tutorial mode
        }}
        tutorialInfo={showSuspectWall ? currentStepModalInfo : null}
      />

      {/* Timeline Dialog */}
      <Timeline
        open={showTimeline}
        onClose={() => setShowTimeline(false)}
      />

      {/* Relationship Map Dialog */}
      <RelationshipMap
        open={showRelationshipMap}
        onClose={() => setShowRelationshipMap(false)}
      />

      {/* Interrogation Modal - Tutorial Mode */}
      <InterrogationModal
        open={showInterrogation}
        onClose={() => {
          setShowInterrogation(false);
          setInterrogationSuspect(null);
        }}
        suspect={interrogationSuspect}
        onComplete={handleInterrogationComplete}
        initialRoundsCompleted={interrogationSuspect ? (interrogationRoundsCompleted[interrogationSuspect.id] || 0) : 0}
        isTutorial={true}
        tutorialNarratives={tutorialNarratives}
        onInterrogationAction={(action, suspectId) => {
          // Forward interrogation sub-step events to parent for tutorial advancement
          if (onActionComplete) {
            onActionComplete(action, suspectId, { success: true });
          }
        }}
        recommendedApproach={
          // Tutorial hints: suggest best approach for each suspect (theme-specific)
          normalizedTheme === 'noir' ? (
            interrogationSuspect?.id === 'suspect_1' ? 'deceptive' :    // Eugene O'Brien - deceptive personality
            interrogationSuspect?.id === 'suspect_2' ? 'sympathetic' :  // Estelle Gambino - nervous personality
            interrogationSuspect?.id === 'suspect_3' ? 'sympathetic' :  // Blanche Caruso - nervous personality
            interrogationSuspect?.id === 'suspect_4' ? 'sympathetic' :  // Harry Cohen - nervous personality
            null
          ) : normalizedTheme === 'miami_vice' ? (
            // Vice roster (from tutorial_vice_complete.json): suspects 1-4 are
            // Trent Vargas, Henri Gallo, Veronica Crawford, Chelsea Fuentes.
            // Approaches must match what the guide narrative tells the user to pick.
            interrogationSuspect?.id === 'suspect_1' ? 'deceptive' :    // Trent Vargas - evasive, deflects (steps 16/19)
            interrogationSuspect?.id === 'suspect_2' ? 'logical' :      // Henri Gallo - nervous (step 68)
            interrogationSuspect?.id === 'suspect_3' ? 'sympathetic' :  // Veronica Crawford - grieving (steps 47/50)
            interrogationSuspect?.id === 'suspect_4' ? 'logical' :      // Chelsea Fuentes - nervous (steps 58/61)
            null
          ) : normalizedTheme === '90s_nyc' ? (
            // 90s_nyc roster (from tutorial_90s_nyc_complete.json): suspects 1-4 are
            // Sakura Robinson, Samantha Santiago, Alicia Johansson, Monica Russo.
            // Approaches must match what the guide narrative tells the user to pick.
            interrogationSuspect?.id === 'suspect_1' ? 'deceptive' :    // Sakura Robinson - deceptive (killer; steps 24/28)
            interrogationSuspect?.id === 'suspect_2' ? 'logical' :      // Samantha Santiago - nervous (steps 46/51)
            interrogationSuspect?.id === 'suspect_3' ? 'sympathetic' :  // Alicia Johansson - grieving (steps 14/19)
            interrogationSuspect?.id === 'suspect_4' ? 'logical' :      // Monica Russo - cooperative (not interrogated in tutorial; harmless default)
            null
          ) : null
        }
      />

      {/* Examine Modal - Tutorial Mode */}
      <ExamineModal
        open={showExamineModal}
        onClose={() => {
          setShowExamineModal(false);
          setExamineLocation(null);
          // Notify parent that examine modal closed
          if (onActionComplete) {
            onActionComplete('modal_examine_closed', examineLocation?.id, { success: true });
          }
        }}
        location={examineLocation}
        onComplete={(result) => {
          // Modal handles API calls and state updates internally
          // Just clear the selection when done
          setSelectedTarget('');
        }}
        usedFocuses={gameState.examinedFocuses?.[examineLocation?.id] || []}
        previousExaminations={gameState.examinations?.[examineLocation?.id]?.length || 0}
        // Tutorial mode event callbacks
        onFocusSelected={(focusId) => {
          if (onActionComplete) {
            onActionComplete('focus_selected', focusId, { success: true });
          }
        }}
        onExaminationStarted={(focuses) => {
          if (onActionComplete) {
            onActionComplete('examination_started', examineLocation?.id, { success: true, focuses });
          }
        }}
        onContinueSearching={() => {
          if (onActionComplete) {
            onActionComplete('continue_examining', examineLocation?.id, { success: true });
          }
        }}
        // Tutorial mode pre-scripted data
        tutorialScriptData={tutorialScript && currentStep ? tutorialScript[currentStep - 1] : null}
      />

      {/* Accusation Modal - Tutorial Mode */}
      <AccusationModal
        sessionId={sessionId}
        isOpen={showAccusation}
        onClose={() => setShowAccusation(false)}
        onSuspectSelect={() => {
          // Advance tutorial when suspect is selected
          if (onActionComplete) {
            onActionComplete('accusation_suspect_selected', null, { success: true });
          }
        }}
        onSubmit={() => {
          // Advance tutorial when accusation is submitted
          if (onActionComplete) {
            onActionComplete('accusation_submitted', null, { success: true });
          }
        }}
        onViewSummary={() => {
          // Advance tutorial when summary is opened
          if (onActionComplete) {
            onActionComplete('view_summary', null, { success: true });
          }
        }}
        onSummaryClose={() => {
          // Advance tutorial when summary modal is closed
          if (onActionComplete) {
            onActionComplete('summary_closed', null, { success: true });
          }
        }}
        onCaseClose={() => {
          // Advance tutorial when case result modal is closed
          if (onActionComplete) {
            onActionComplete('case_closed', null, { success: true });
          }
        }}
      />

      {/* Cipher Modal - Tutorial Mode. Uses normalizedTheme so 'vice' aliases
          to 'miami_vice' for the TUTORIAL_CIPHER_DATA lookup and the theme
          prop, even if the store is somehow not normalized. */}
      <TutorialCipherModal
        open={showCipherModal}
        onClose={() => {
          setShowCipherModal(false);
          if (onActionComplete) {
            onActionComplete('cipher_modal_closed', null, { success: true });
          }
        }}
        ciphertext={TUTORIAL_CIPHER_DATA[normalizedTheme]?.ciphertext || ''}
        hints={TUTORIAL_CIPHER_DATA[normalizedTheme]?.hints || []}
        expectedSolution={TUTORIAL_CIPHER_DATA[normalizedTheme]?.expectedSolution || ''}
        onSubmit={(solution) => {
          if (onActionComplete) {
            onActionComplete('cipher_solution_entered', null, { success: true, solution });
          }
        }}
        theme={normalizedTheme}
      />
    </div>
  );
};
